// NewsEditor API Service
// Handles all backend communication for the NewsEditor system

import { NewsJob, NewsAggregation, NewsEvent } from '../types';

class NewsEditorApiService {
  private baseUrl = '/news/api';

  /**
   * Get all available news jobs
   */
  async getJobs(): Promise<NewsJob[]> {
    try {
      const response = await fetch(`${this.baseUrl}/jobs/`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.jobs || [];
    } catch (error) {
      console.error('Error fetching jobs:', error);
      throw new Error('Failed to fetch news jobs');
    }
  }

  /**
   * Get news aggregation data for a specific job
   */
  async getNewsAggregation(jobId: string): Promise<NewsAggregation> {
    try {
      // Try the direct file path first (like NewsTheater does)
      const response = await fetch(`/news/out/${jobId}/newsevent_stage/newsevent_stage_aggregation.json`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data: NewsAggregation = await response.json();
      console.log('Direct file fetch result:', data);
      return data;
    } catch (error) {
      console.error('Error fetching news aggregation from file:', error);
      // Fallback to API endpoint if direct file access fails
      try {
        const response = await fetch(`${this.baseUrl}/jobs/${jobId}/headlines`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log('API fallback result:', data);
        return data.data;
      } catch (fallbackError) {
        console.error('Fallback API call also failed:', fallbackError);
        // Return empty aggregation if both methods fail
        return {
          news_events: [],
          metadata: {
            total_events: 0,
            last_updated: new Date().toISOString(),
            search_query: '',
            search_level: 'medium'
          }
        };
      }
    }
  }

  /**
   * Get job status and stage information
   */
  async getJobStatus(jobId: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/jobs/${jobId}/status`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching job status:', error);
      throw new Error('Failed to fetch job status');
    }
  }

  /**
   * Get job metadata including query topic, location, search level, etc.
   */
  async getJobMetadata(jobId: string): Promise<any> {
    try {
      const response = await fetch(`/news/out/${jobId}/job_metadata.json`);
      if (!response.ok) {
        // Return null if job metadata doesn't exist (not all jobs have this)
        return null;
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching job metadata:', error);
      // Return null if job metadata doesn't exist or can't be loaded
      return null;
    }
  }

  /**
   * Update news events for a job
   */
  async updateNewsEvents(jobId: string, events: NewsEvent[]): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/jobs/${jobId}/events`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ events }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error updating news events:', error);
      throw new Error('Failed to update news events');
    }
  }

  /**
   * Add a new news event to a job
   */
  async addNewsEvent(jobId: string, event: Omit<NewsEvent, 'id'>): Promise<NewsEvent> {
    try {
      const response = await fetch(`${this.baseUrl}/jobs/${jobId}/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.event;
    } catch (error) {
      console.error('Error adding news event:', error);
      throw new Error('Failed to add news event');
    }
  }

  /**
   * Delete a news event from a job
   */
  async deleteNewsEvent(jobId: string, eventId: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/jobs/${jobId}/events/${eventId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting news event:', error);
      throw new Error('Failed to delete news event');
    }
  }

  /**
   * Rerun a specific stage for a job
   */
  async getScriptGeneration(jobId: string): Promise<any> {
    try {
      // Try to load from direct file path first - using the correct path structure
      const response = await fetch(`/news/out/${jobId}/scriptgen_stage/scriptgen_stage_script.json`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Direct file fetch result for script generation:', data);

      // Transform the data to match our expected format
      if (data.show_script && data.show_script.scenes) {
        const transformedScenes = data.show_script.scenes.map((scene: any, index: number) => ({
          id: scene.id || `scene-${index}`,
          name: scene.type || 'Unnamed Scene',
          type: scene.type || 'headline',
          duration_sec: scene.duration_sec || 30, // Default duration if not specified
          dialogue: scene.dialogue || '',
          transition: scene.parenthetical || '',
          mood: 'neutral' // Default mood if not specified
        }));

        const transformedScript = {
          scenes: transformedScenes,
          metadata: {
            total_duration: transformedScenes.reduce((sum: number, scene: any) => sum + scene.duration_sec, 0),
            scene_count: transformedScenes.length,
            last_updated: data.metadata?.created_at || new Date().toISOString()
          }
        };

        console.log('Transformed script generation data:', transformedScript);
        return transformedScript;
      }

      return {
        scenes: [],
        metadata: {
          total_duration: 0,
          scene_count: 0,
          last_updated: new Date().toISOString()
        }
      };
    } catch (error) {
      console.error('Error fetching script generation from file:', error);
      try {
        // Fallback to API endpoint
        const response = await fetch(`${this.baseUrl}/jobs/${jobId}/script`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log('API fallback result for script generation:', data);
        return data.data || data;
      } catch (fallbackError) {
        console.error('Fallback API call also failed for script generation:', fallbackError);
        // Return empty script if both methods fail
        return {
          scenes: [],
          metadata: {
            total_duration: 0,
            scene_count: 0,
            last_updated: new Date().toISOString()
          }
        };
      }
    }
  }

    /**
   * Trigger script generation for a job (rerun from news events)
   * Returns immediately with job status, processing continues in background
   */
  async triggerScriptGeneration(jobId: string): Promise<{ success: boolean; message: string; job_id: string }> {
    try {
      console.log(`Triggering script generation for job ${jobId}`);

      const requestBody = {
        model: "gpt-4o",
        max_scenes: 8,
        preferred_scene_duration: 45,
        editorial_tone: "professional"
      };

      const response = await fetch(`/news/api/scriptgen/${jobId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Script generation response error:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}, response: ${errorText}`);
      }

      const data = await response.json();
      console.log('Script generation triggered successfully:', data);

      return {
        success: data.success || true,
        message: data.message || 'Script generation started successfully',
        job_id: data.job_id || jobId
      };
    } catch (error) {
      console.error('Error triggering script generation:', error);
      throw new Error(`Failed to trigger script generation: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Check script generation status for a job
   * Based on actual backend implementation: file-based completion, no real progress
   */
  async getScriptGenerationStatus(jobId: string): Promise<{ status: string; progress: number; message: string }> {
    try {
      // Use the canonical jobs status endpoint - this is what backend actually provides
      const jobStatusResp = await fetch(`${this.baseUrl}/jobs/${jobId}/status`);
      if (jobStatusResp.ok) {
        const jobStatus = await jobStatusResp.json();
        const stage = jobStatus.stages?.scriptgen;

        if (stage) {
          const stageStatus: string = (stage.status || '').toLowerCase();
          const completed: boolean = !!stage.completed;

          // Map backend status directly - no progress calculation needed
          if (completed || stageStatus === 'completed') {
            return {
              status: 'completed',
              progress: 100,
              message: 'Script generation completed successfully'
            };
          } else if (stageStatus === 'in_progress') {
            return {
              status: 'processing',
              progress: 50, // Backend doesn't provide real progress, use fixed value
              message: 'Script generation in progress...'
            };
          } else if (stageStatus === 'not_started') {
            return {
              status: 'processing',
              progress: 25, // Backend doesn't provide real progress, use fixed value
              message: 'Script generation not started yet...'
            };
          } else {
            // Unknown status - treat as processing to avoid false failures
            return {
              status: 'processing',
              progress: 50,
              message: `Status: ${stageStatus || 'unknown'}`
            };
          }
        }
      }

      // Fallback: check if script artifacts exist (what backend actually tracks)
      try {
        const artifactsResp = await fetch(`/news/api/scriptgen/${jobId}/artifacts`);
        if (artifactsResp.ok) {
          const data = await artifactsResp.json();
          if (data.script_exists) {
            return {
              status: 'completed',
              progress: 100,
              message: 'Script artifacts ready'
            };
          } else {
            return {
              status: 'processing',
              progress: 50,
              message: 'Script generation in progress...'
            };
          }
        }
      } catch (artifactsError) {
        // 404 is expected during generation, don't treat as error
        console.log(`Artifacts not ready yet for ${jobId}:`, artifactsError);
      }

      // Default: treat as processing (not failed) since backend doesn't provide failure status
      return {
        status: 'processing',
        progress: 50,
        message: 'Checking script generation status...'
      };
    } catch (error) {
      console.warn('Error checking script generation status, treating as in-progress:', error);
      // Network errors should not mark as failed - backend doesn't provide failure status
      return {
        status: 'processing',
        progress: 50,
        message: 'Checking status...'
      };
    }
  }

  /**
   * Save updated script generation data back to the backend
   * Preserves the exact JSON structure and semantics
   */
  async saveScriptGeneration(jobId: string, script: any): Promise<void> {
    try {
      // Reconstruct the backend JSON structure exactly as it was
      const backendData = {
        show_script: {
          scenes: script.scenes.map((scene: any) => ({
            id: scene.id,
            name: scene.name,
            type: scene.type,
            duration_sec: scene.duration_sec,
            dialogue: scene.dialogue,
            transition: scene.transition,
            mood: scene.mood
          }))
        },
        metadata: {
          last_updated: new Date().toISOString(),
          total_duration: script.metadata.total_duration,
          scene_count: script.metadata.scene_count
        }
      };

      console.log('Attempting to save script with data:', backendData);
      console.log('Save URL:', `/news/api/scriptgen/${jobId}/script`);

      // Use the new script update endpoint
      const response = await fetch(`/news/api/scriptgen/${jobId}/script`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(backendData, null, 2)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Save response error:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}, response: ${errorText}`);
      }

      console.log('Script generation data saved successfully');
    } catch (error) {
      console.error('Error saving script generation:', error);
      throw new Error(`Failed to save script generation data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async rerunStage(jobId: string, stage: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/jobs/${jobId}/rerun`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ stage }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error rerunning stage:', error);
      throw new Error('Failed to rerun stage');
    }
  }

  /**
   * Trigger rendering with cast selection
   */
  async triggerRendering(jobId: string, castAgent: any, studioLocation: any, enableOutpaint: boolean = false): Promise<void> {
    try {
      console.log(`Triggering rendering for job ${jobId} with cast: ${castAgent.name}, studio: ${studioLocation.name}`);

      // Use the renderer endpoint to trigger rendering with cast information
      const requestBody = {
        job_uuid: jobId,
        character_id: castAgent.id,
        config: {
          output_format: "mp4",
          quality: "production",
          resolution: "1920x1080",
          target_bitrate: "5000k",
          include_captions: true,
          include_broll: true,
          background_template: studioLocation.id,
          enable_outpaint: enableOutpaint
        }
      };

      const response = await fetch(`/news/api/renderer/${jobId}/render`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        // Log the response text to see what we're getting
        const responseText = await response.text();
        console.error('API Response Text:', responseText);
        throw new Error(`HTTP error! status: ${response.status}, response: ${responseText}`);
      }

      // Check if response has content before parsing JSON
      const responseText = await response.text();
      console.log('Raw API Response:', responseText);

      let data;
      if (responseText.trim()) {
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          console.error('JSON Parse Error:', parseError);
          console.error('Response Text:', responseText);
          throw new Error(`Invalid JSON response: ${responseText}`);
        }
      } else {
        data = {}; // Empty response
      }

      console.log('Rendering started successfully:', data);
    } catch (error) {
      console.error('Error triggering rendering:', error);
      throw error;
    }
  }

  /**
   * Check rendering status for a job
   */
  async getRenderingStatus(jobId: string): Promise<any> {
    try {
      const response = await fetch(`/news/api/renderer/${jobId}/artifacts`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching rendering status:', error);
      throw new Error('Failed to fetch rendering status');
    }
  }

    /**
   * Trigger composition generation for both portrait and landscape modes
   */
  async triggerComposition(jobId: string): Promise<void> {
    try {
      console.log(`Triggering composition generation for job ${jobId}`);
      console.log(`Using base URL: ${this.baseUrl}`);

      // Trigger composition for both portrait and landscape modes
      const portraitPromise = this.triggerCompositionMode(jobId, 'portrait');
      const landscapePromise = this.triggerCompositionMode(jobId, 'landscape');

      // Wait for both to start
      await Promise.all([portraitPromise, landscapePromise]);

      console.log('Composition generation started for both modes');
    } catch (error) {
      console.error('Error triggering composition:', error);
      throw new Error('Failed to trigger composition generation');
    }
  }

  /**
   * Trigger composition for a specific orientation
   */
  private async triggerCompositionMode(jobId: string, orientation: 'portrait' | 'landscape'): Promise<void> {
    try {
      const requestBody = {
        job_uuid: jobId,
        render_reference: jobId, // Use job ID as render reference
        output_format: "mp4",
        quality: "production",
        resolution_width: orientation === 'portrait' ? 720 : 1280,
        resolution_height: orientation === 'portrait' ? 1280 : 720,
        include_transitions: true,
        include_audio: true,
        include_subtitles: false
      };

      const response = await fetch(`${this.baseUrl}/composition/${jobId}/compose_v2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(`Composition started for ${orientation}:`, data);
    } catch (error) {
      console.error(`Error triggering ${orientation} composition:`, error);
      throw error;
    }
  }

    /**
   * Check composition generation status
   */
  async getCompositionStatus(jobId: string): Promise<any> {
    try {
      // Use the composition-specific status endpoint
      const response = await fetch(`${this.baseUrl}/composition/${jobId}/status`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      // Map the composition status response
      return {
        status: data.status || 'unknown',
        progress: data.completed ? 100 : (data.status === 'in_progress' ? 50 : 0),
        last_updated: new Date().toISOString(),
        completed: data.completed || false,
        artifacts_count: data.artifacts_count || 0
      };
    } catch (error) {
      console.error('Error fetching composition status:', error);
      throw new Error('Failed to fetch composition status');
    }
  }
}

export const newsEditorApi = new NewsEditorApiService();
export default newsEditorApi;
